/**
 * Render Question Scoring
 *
 * Centralized utility for rendering question components in scoring mode.
 * Orchestrates the adapter → component → render pipeline.
 */

import React from 'react';
import { TeamScoringQuestion } from '@/components/scoring/TeamScoringQuestion';
import { PlayerScoringQuestion } from '@/components/scoring/PlayerScoringQuestion';
import { PropScoringQuestion } from '@/components/scoring/PropScoringQuestion';
import { OverUnderScoringQuestion } from '@/components/scoring/OverUnderScoringQuestion';
import { TiebreakerScoringQuestion } from '@/components/scoring/TiebreakerScoringQuestion';
import { CustomScoringQuestion } from '@/components/scoring/CustomScoringQuestion';
import { LeaderScoringQuestion } from '@/components/scoring/LeaderScoringQuestion';
import {
  getScoringAdapter,
  isValidScoringQuestionType,
  type ScoringQuestionType,
} from './adapterRegistry';
import type { RenderableQuestionType } from '../questionRenderTypes';
import type { ScoringQuestionData } from './types';
import type { teamScoringAdapter } from './adapters/teamScoringAdapter';
import type { playerScoringAdapter } from './adapters/playerScoringAdapter';
import type { propScoringAdapter } from './adapters/propScoringAdapter';
import type { overUnderScoringAdapter } from './adapters/overUnderScoringAdapter';
import type { tiebreakerScoringAdapter } from './adapters/tiebreakerScoringAdapter';
import type { customScoringAdapter } from './adapters/customScoringAdapter';
import type { leaderScoringAdapter } from './adapters/leaderScoringAdapter';

/**
 * Union type of all possible adapter return types for type safety
 */
type AdapterProps =
  | ReturnType<typeof teamScoringAdapter>
  | ReturnType<typeof playerScoringAdapter>
  | ReturnType<typeof propScoringAdapter>
  | ReturnType<typeof overUnderScoringAdapter>
  | ReturnType<typeof tiebreakerScoringAdapter>
  | ReturnType<typeof customScoringAdapter>
  | ReturnType<typeof leaderScoringAdapter>;

/**
 * Component map - Maps question types to their React components
 */
export const COMPONENT_MAP = {
  team: TeamScoringQuestion,
  player: PlayerScoringQuestion,
  prop: PropScoringQuestion,
  over_under: OverUnderScoringQuestion,
  team_over_under: OverUnderScoringQuestion,
  player_over_under: OverUnderScoringQuestion,
  tiebreaker: TiebreakerScoringQuestion,
  custom: CustomScoringQuestion,
  player_leader: LeaderScoringQuestion,
  team_leader: LeaderScoringQuestion,
  // Main markets (DKPLAT-500): Moneyline/Spread render as option-picks (Team
  // scoring component); Total is a match-level Over/Under.
  moneyline: TeamScoringQuestion,
  spread: TeamScoringQuestion,
  total: OverUnderScoringQuestion,
  // Exhaustive over RenderableQuestionType at compile time — see adapterRegistry.
} satisfies Record<RenderableQuestionType, React.ElementType>;

interface RenderQuestionScoringOptions {
  questionId?: string;
  fixtureId?: string;
  onMarkCorrect?: (optionId: string, isCorrect: boolean) => void;
  onSave?: (optionId: string) => void;
  onCancel?: (optionId: string) => void;
  language?: 'en' | 'fr';
  manualScoring?: boolean;
  matchStats?: import('@/api/feed/getFeedMatchStats').MatchStatsResponse;
  onStatChange?: (optionId: string, statValue: number) => void;
  onOverrideSuccess?: () => void;
}

/**
 * Renders a question component for scoring
 *
 * Pipeline:
 * 1. Validate question type
 * 2. Get adapter for type
 * 3. Transform data → component props
 * 4. Render component
 *
 * @param data - Scoring question data
 * @param options - Additional options for rendering
 * @returns Rendered React component or error message
 */
export function renderQuestionScoring(
  data: ScoringQuestionData,
  options: RenderQuestionScoringOptions = {}
): React.ReactNode {
  const questionType = data.questionType as ScoringQuestionType;

  // Validate question type
  if (!isValidScoringQuestionType(questionType)) {
    return (
      <div className="text-red-500 p-4 text-center">
        Unsupported question type: <span className="font-mono">{data.questionType}</span>
      </div>
    );
  }

  // Get adapter and component
  const adapter = getScoringAdapter(questionType);
  const Component = COMPONENT_MAP[questionType];

  if (!adapter || !Component) {
    return (
      <div className="text-red-500 p-4 text-center">
        No adapter or component found for type: <span className="font-mono">{questionType}</span>
      </div>
    );
  }

  // Transform data to component props
  let props: AdapterProps;
  try {
    props = adapter(data);
  } catch (error) {
    return (
      <div className="text-red-500 p-4 text-center">
        <p>Error transforming question data:</p>
        <p className="font-mono text-sm mt-2">
          {error instanceof Error ? error.message : String(error)}
        </p>
      </div>
    );
  }

  // Render component with additional options
  return <Component {...(props as any)} {...options} />;
}
